import { Box, Stack, Typography, Button, IconButton, Grid, alpha } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { colors, shadows } from "../source/source.styles";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CalendarTodayOutlinedIcon from "@mui/icons-material/CalendarTodayOutlined";
import FileDownloadOutlinedIcon from "@mui/icons-material/FileDownloadOutlined";
import TrendingUpIcon from "@mui/icons-material/TrendingUp";
import TrendingDownIcon from "@mui/icons-material/TrendingDown";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  ComposedChart,
} from "recharts";

const MetricCard = ({ label, value, change, changeType, icon }) => {
  const isPositive = changeType === "up";
  return (
    <Box
      sx={{
        bgcolor: "#FFFFFF",
        borderRadius: 3,
        p: 3,
        border: `1px solid ${colors.borderLight}`,
        boxShadow: shadows.card,
      }}
    >
      <Typography
        sx={{
          fontSize: 11,
          fontWeight: 700,
          letterSpacing: 0.5,
          color: colors.textMuted,
          textTransform: "uppercase",
          mb: 1,
        }}
      >
        {label}
      </Typography>
      <Stack direction="row" alignItems="center" spacing={1}>
        <Typography
          sx={{
            fontSize: 32,
            fontWeight: 700,
            color: colors.textPrimary,
          }}
        >
          {value}
        </Typography>
        <Stack
          direction="row"
          alignItems="center"
          spacing={0.5}
          sx={{
            px: 1,
            py: 0.5,
            borderRadius: 1,
            bgcolor: isPositive ? "#ECFDF5" : "#FEF2F2",
            color: isPositive ? colors.success : colors.danger,
          }}
        >
          {isPositive ? (
            <TrendingUpIcon sx={{ fontSize: 14 }} />
          ) : (
            <TrendingDownIcon sx={{ fontSize: 14 }} />
          )}
          <Typography sx={{ fontSize: 12, fontWeight: 600 }}>
            {change}
          </Typography>
        </Stack>
      </Stack>
    </Box>
  );
};

const DetailedSourceReport = ({ source, onBack }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;

  // Mock data - replace with actual API data
  const trendData = [
    { date: "May 01", leads: 20, conversions: 5 },
    { date: "May 07", leads: 30, conversions: 8 },
    { date: "May 14", leads: 45, conversions: 12 },
    { date: "May 21", leads: 38, conversions: 10 },
    { date: "May 31", leads: 25, conversions: 7 },
  ];

  const qualityData = [
    { name: "Hot", value: 45, color: "#EF4444" },
    { name: "Warm", value: 30, color: "#F59E0B" },
    { name: "Cold", value: 25, color: "#3B82F6" },
  ];

  const funnelData = [
    { stage: "Lead Generation", count: 542, percentage: 100, color: primaryColor, change: "-24.1%" },
    { stage: "Qualification", count: 409, percentage: 75, color: primaryColor, change: "-34.2%" },
    { stage: "Consultation", count: 183, percentage: 34, color: primaryColor, change: "-34.4%" },
    { stage: "Won Deal", count: 100, percentage: 18, color: "#10B981", change: "" },
  ];

  return (
    <Box
      sx={{
        bgcolor: colors.bgPrimary,
        minHeight: "100vh",
      }}
    >
      {/* Header */}
      <Box sx={{ bgcolor: "#FFFFFF", borderBottom: `1px solid ${colors.border}`, p: 3 }}>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={onBack}
              sx={{
                color: colors.textMuted,
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                },
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Stack direction="row" alignItems="center" spacing={0.5} sx={{ mb: 0.5 }}>
                <Typography
                  component="span"
                  onClick={onBack}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    cursor: "pointer",
                    "&:hover": {
                      color: colors.primary,
                    },
                  }}
                >
                  REPORTS
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                  }}
                >
                  ›
                </Typography>
                <Typography
                  component="span"
                  onClick={onBack}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    cursor: "pointer",
                    "&:hover": {
                      color: colors.primary,
                    },
                  }}
                >
                  SOURCES
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                  }}
                >
                  ›
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 700,
                    color: colors.primary,
                    letterSpacing: 0.5,
                  }}
                >
                  {source?.name?.toUpperCase() || "SOURCE"}
                </Typography>
              </Stack>
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Leads by {source?.name || "Source"} Performance
              </Typography>
            </Box>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<CalendarTodayOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                px: 2,
                py: 0.75,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 500,
                fontSize: 13,
                borderColor: colors.border,
                color: colors.textSecondary,
              }}
            >
              Last 30 Days
            </Button>
            <Button
              variant="contained"
              startIcon={<FileDownloadOutlinedIcon sx={{ fontSize: 16 }} />}
              sx={{
                px: 2.5,
                py: 0.75,
                borderRadius: 2,
                textTransform: "none",
                fontWeight: 600,
                fontSize: 13,
                bgcolor: colors.primary,
                color: "#FFFFFF",
                boxShadow: "none",
              }}
            >
              Export Report
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ p: 4 }}>
        {/* Metric Cards */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard
              label="Total Leads"
              value={source?.totalLeads || 542}
              change="+12%"
              changeType="up"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard
              label="Avg. Cost Per Lead"
              value="₹340"
              change="-8%"
              changeType="down"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard
              label="Conversion Rate"
              value={`${source?.conversionRate || 18.4}%`}
              change="+23%"
              changeType="up"
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 6, lg: 3 }}>
            <MetricCard
              label="Generated Revenue"
              value={`₹${source?.revenue?.toLocaleString() || "2,48,500"}`}
              change="+18%"
              changeType="up"
            />
          </Grid>
      </Grid>

      {/* Lead vs Conversion Trend Chart */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderRadius: 3,
          p: 3,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: shadows.card,
          mb: 4,
        }}
      >
        <Typography
          sx={{
            fontSize: 16,
            fontWeight: 700,
            color: colors.textPrimary,
            mb: 1,
          }}
        >
          Lead vs Conversion Trend
        </Typography>
        <Typography
          sx={{
            fontSize: 13,
            color: colors.textSecondary,
            mb: 3,
          }}
        >
          Daily trend analysis for the current billing cycle
        </Typography>

        <ResponsiveContainer width="100%" height={300}>
          <ComposedChart data={trendData}>
            <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} vertical={false} />
            <XAxis
              dataKey="date"
              tick={{ fontSize: 11, fill: colors.textSecondary }}
              stroke={colors.border}
              axisLine={false}
            />
            <YAxis
              tick={{ fontSize: 11, fill: colors.textSecondary }}
              stroke={colors.border}
              axisLine={false}
            />
            <Tooltip />
            <Legend />
            <Bar
              dataKey="leads"
              name="Leads"
              fill={alpha(primaryColor, 0.4)}
              radius={[4, 4, 0, 0]}
              barSize={40}
            />
            <Line
              type="natural"
              dataKey="leads"
              name="Lead Trend"
              stroke="#10B981"
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
            />
            <Line
              type="natural"
              dataKey="conversions"
              name="Conversion Trend"
              stroke={primaryColor}
              strokeWidth={3}
              dot={false}
              activeDot={{ r: 6 }}
            />
          </ComposedChart>
        </ResponsiveContainer>
      </Box>

      {/* Bottom Section: Quality Score and Funnel */}
      <Grid container spacing={3}>
        {/* Lead Quality Score Breakdown */}
        <Grid size={{ xs: 12, md: 5 }}>
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
              height: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Lead Quality Score Breakdown
            </Typography>

            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
              <ResponsiveContainer width={200} height={200}>
                <PieChart>
                  <Pie
                    data={qualityData}
                    innerRadius={60}
                    outerRadius={100}
                    dataKey="value"
                  >
                    {qualityData.map((entry, index) => (
                      <Cell key={`cell-${index}`} fill={entry.color} />
                    ))}
                  </Pie>
                </PieChart>
              </ResponsiveContainer>
              <Box
                sx={{
                  position: "absolute",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Typography sx={{ fontSize: 32, fontWeight: 700, color: colors.textPrimary }}>
                  542
                </Typography>
                <Typography sx={{ fontSize: 13, color: colors.textMuted }}>
                  LEADS
                </Typography>
              </Box>
            </Box>

            <Stack spacing={1.5} sx={{ mt: 3 }}>
              {qualityData.map((item) => (
                <Stack key={item.name} direction="row" alignItems="center" justifyContent="space-between">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Box
                      sx={{
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        bgcolor: item.color,
                      }}
                    />
                    <Typography sx={{ fontSize: 14, fontWeight: 500, color: colors.textPrimary }}>
                      {item.name}
                    </Typography>
                  </Stack>
                  <Typography sx={{ fontSize: 14, fontWeight: 600, color: colors.textPrimary }}>
                    {item.value}%
                  </Typography>
                </Stack>
              ))}
            </Stack>
          </Box>
        </Grid>

        {/* Detailed Funnel Performance */}
        <Grid size={{ xs: 12, md: 7 }}>
          <Box
            sx={{
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
              height: "100%",
            }}
          >
            <Typography
              sx={{
                fontSize: 16,
                fontWeight: 700,
                color: colors.textPrimary,
                mb: 3,
              }}
            >
              Detailed Funnel Performance
            </Typography>

            <Stack spacing={3}>
              {funnelData.map((stage, index) => (
                <Box key={stage.stage}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                      {stage.stage}
                    </Typography>
                    <Typography sx={{ fontSize: 14, fontWeight: 700, color: colors.textPrimary }}>
                      {stage.count}
                    </Typography>
                  </Stack>
                  <Box sx={{ position: "relative", width: "100%", overflow: "hidden" }}>
                    <Box
                      sx={{
                        width: `${stage.percentage}%`,
                        maxWidth: "100%",
                        height: 40,
                        borderRadius: 2,
                        bgcolor: stage.color,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                        px: 2,
                        boxSizing: "border-box",
                      }}
                    >
                      <Typography sx={{ fontSize: 12, fontWeight: 600, color: "#FFFFFF" }}>
                        {index === 0 ? "BASELINE 100%" : `${stage.percentage}%`}
                      </Typography>
                      {stage.change && (
                        <Typography sx={{ fontSize: 11, fontWeight: 600, color: "#FFFFFF" }}>
                          {stage.change}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>
      </Box>
    </Box>
  );
};

export default DetailedSourceReport;
